// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Button, Icon, Typography } from '@material-ui/core';

// Internal Dependencies
import Photos from './photos';
import OverLoading from '../../global/OverLoading';
import RadioBtnSelect from '../../common/radioBtnSelect';

import apiService from '../../../services.js/apiService';
import { IfTrueThrowError, IsEmpty } from '../../../common/checks';
import { Status } from '../../../common/constants';
import { Sleep } from '../../../common/util';

const FoodReviewORGenericHighlight = ({ pageId, pageName, heading, isGeneric }) => {
  const [loading, setLoading] = useState(true);
  const [pageHighlight, setPageHighlight] = useState(null);

  const getPageHighlight = async () => {
    try {
      setLoading(true);
      let payload = {};

      if (isGeneric) {
        payload = { pageId, isGeneric: true };
      } else {
        payload = { pageId, isFoodReview: true };
      }

      const data = await apiService.getPageHighlights(payload);
      if (data.totalCount >= 1) {
        setPageHighlight(data.rows[0]);
      }

      setLoading(false);
    } catch (error) {
      toast.error('Error while fetching data for partner buildings');
      console.log(error);
    }
  };

  const createPageHighlight = async () => {
    try {
      setLoading(true);
      let payload = {};

      if (isGeneric) {
        payload = { pageId, title: pageName, isGeneric: true };
      } else {
        payload = { pageId, title: 'Food Review', isFoodReview: true };
      }

      const data = await apiService.createPageHighlight(payload);
      setPageHighlight(data);
      setLoading(false);
    } catch (error) {
      toast.error(`Error while saving data for partner buildings: ${error.message}`);
      console.log(error);
    }
  };

  const validatePageHighlight = () => {
    IfTrueThrowError(IsEmpty(pageHighlight.title), 'Title is required');

    if (!IsEmpty(pageHighlight.images)) {
      const hasEmptyDisplay = pageHighlight.images.some((val) => IsEmpty(val.display));
      IfTrueThrowError(!IsEmpty(pageHighlight.images) && hasEmptyDisplay, 'Please crop all image(s) before saving the pageHighlight');
    }
  };

  const updatePageHighlight = async () => {
    try {
      setLoading(true);
      await Sleep(200);

      validatePageHighlight();
      await apiService.updatePageHighlight(pageHighlight.id, pageHighlight);
      toast.success(`${isGeneric ? 'Generic' : 'Food Review'} Updated`);
    } catch (error) {
      toast.error(error.message, { autoClose: 7000 });
    } finally {
      setLoading(false);
    }
  };

  const onPageHighlightImageChange = (images) => {
    if (images !== pageHighlight.images) {
      pageHighlight.images = images;
      setPageHighlight({ ...pageHighlight });
    }
  };

  useEffect(() => {
    getPageHighlight();
  }, []);

  return (
    <>
      {loading && <OverLoading />}

      <div className="row">
        <div className="col-12 mb-3">
          <h3>
            {heading}
            {' '}
            Highlight
          </h3>
        </div>
        {
        IsEmpty(pageHighlight)
          ? (
            <div className="col-12 mb-3">
              <Button onClick={createPageHighlight} variant="contained" style={{ backgroundColor: '#13B0F1', color: 'white', width: 120 }}>
                <Icon style={{ marginRight: 5 }}>add</Icon>
                {' '}
                Create
              </Button>
            </div>
          ) : (
            <div className="col-12 col-md-8">
              <form>
                <div className="form-group">
                  <Typography>Title *</Typography>
                  <input
                    type="text"
                    className="form-control mt-2"
                    name="title"
                    value={pageHighlight.title}
                    onChange={(e) => {
                          setPageHighlight({ ...pageHighlight, title: e.target.value });
                    }}
                    required
                  />
                </div>
                <div className="form-group mt-3 mb-0">
                  <Typography>Status *</Typography>
                  <RadioBtnSelect
                    className="ml-2"
                    value={pageHighlight.status}
                    options={[
                      { value: Status.Draft },
                      { value: Status.Pending },
                      { value: Status.Live },
                      { value: Status.Rejected },
                    ]}
                    onChange={(status) => setPageHighlight({ ...pageHighlight, status })}
                  />
                </div>
                <div className="form-group mb-1 pb-2" style={{ borderBottom: '1px solid gray' }}>
                  <Typography>Type</Typography>
                  <RadioBtnSelect
                    className="ml-2"
                    value={`${heading} Highlight`}
                    options={[
                      { value: `${heading} Highlight` },
                    ]}
                  />
                </div>
                <div className="form-group">
                  <Photos
                    pageId={pageHighlight.pageId}
                    pageHighlightId={pageHighlight.id}
                    images={pageHighlight.images}
                    onSort={(images) => onPageHighlightImageChange(images)}
                    onPageHighlightImageChange={(images) => onPageHighlightImageChange(images)}
                  />
                </div>
                <div className="form-group text-center mt-5">
                  <Button variant="contained" onClick={updatePageHighlight} color="secondary">
                    <Typography className="px-4" variant="body1">Update Info</Typography>
                  </Button>
                </div>
              </form>
            </div>
          )
        }
      </div>
    </>
  );
};

export default FoodReviewORGenericHighlight;

FoodReviewORGenericHighlight.propTypes = {
  isGeneric: PropTypes.bool.isRequired,
  pageId:    PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  pageName: PropTypes.string,
  heading:  PropTypes.string.isRequired,
};

FoodReviewORGenericHighlight.defaultProps = {
  pageName: null,
};
